/** @file Utilities for downloading files as binary data. */
import * as codec from 'purify-ts/Codec';
import * as compression from '@codegrade/compression';
import { parsers } from '@codegrade/utils';
import client from '@/client';
import { parseBool } from './boolean';
import { withParsedApiErrors } from './error';
/**
 * Fetch a route as an arraybuffer, with auth token injection and retry.
 *
 * Uses the {@link client} so that same-origin requests get the Bearer
 * token and the standard error-handling pipeline. Error responses are
 * parsed via {@link withParsedApiErrors} so that getErrorMessage
 * returns the server's message rather than a generic HTTP status.
 *
 * @param route An absolute path (e.g., `/api/v1/code/42`) or a full
 *              URL (e.g., an S3 signed link). External URLs will not
 *              receive an auth header.
 * @returns The response body as a `Uint8Array` together with the
 *          response headers.
 */
async function getArrayBuffer(route) {
    return withParsedApiErrors(client.rawRequest({
        method: 'GET',
        path: route,
        responses: {
            200: async (response) => {
                const buffer = await parsers.parseArrayBufferResponse(response);
                return { data: new Uint8Array(buffer), headers: response.headers };
            },
        },
    }));
}
// https://stackoverflow.com/questions/13405129/javascript-create-and-save-file
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function downloadFile(data, filename, contentType) {
    const file = new Blob([data], { type: contentType });
    const url = URL.createObjectURL(file);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }, 0);
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function downloadMirrorFile(file) {
    window.open(file.url);
}
const SignedUrlCodec = codec.Codec.interface({ url: codec.string });
/**
 * Retrieves a File from S3 storage as an ArrayBuffer using Axios starting form either the API
 * route to retrieve the s3 signed link, or the signed link itself.
 *
 * @param route The API route to retrieve the S3 signed link or the the link itself.
 * @returns Either an error occurring during the operation or the ArrayBuffer with the file data.
 */
export async function downloadRawFile(route) {
    const response = await getArrayBuffer(route);
    const headers = response.headers;
    if (headers != null && parseBool(headers.get('x-cg-is-url') ?? '', false)) {
        /* As the data is an array buffer, we need to decode the data before parsing. */
        const parsedResponseData = JSON.parse(new TextDecoder().decode(response.data));
        const decoded = SignedUrlCodec.decode(parsedResponseData);
        if (!decoded.isRight()) {
            throw new Error(decoded.extract().toString());
        }
        const { data } = await getArrayBuffer(decoded.extract().url);
        return data;
    }
    return response.data;
}
/**
 * Downloads a file from an S3 signed link.
 *
 * @param signedS3link The signed S3 link to download the file from.
 * @returns The file as an `ArrayBuffer`, or an error if the get fails.
 */
export async function downloadFileFromS3(signedS3link) {
    const { data } = await getArrayBuffer(signedS3link);
    return data;
}
/**
 * Downloads a compressed file from an S3 signed link and decompresses it.
 *
 * @param signedS3link The signed S3 link to download the file from.
 * @returns The file as a `Uint8Array`, or an error if the get fails.
 */
export async function downloadCompressedFileFromS3(signedS3link) {
    const data = await downloadFileFromS3(signedS3link);
    const compressed = compression.CompressionResult.fromCompressedBinary(new Uint8Array(data)).unsafeCoerce();
    return await compressed.decompressPromise();
}
